class FitRightV2 extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector("[data-fit-right-form]");
    this.feedback = this.querySelector("[data-fit-right-feedback]");
    this.sitboneInput = this.querySelector("[data-fit-right-sitbone-mm]");
    this.resultsContainer = this.querySelector("[data-fit-right-results]");
    this.resultsList = this.querySelector("[data-fit-right-results-list]");
    this.dataEl = this.querySelector("[data-fit-right-data]");
    this.optionButtons = Array.from(this.querySelectorAll(".fit-right-v2__option"));
    this.templates = Array.from(this.querySelectorAll("[data-product-template]"));

    if (!this.form || !this.feedback || !this.sitboneInput || !this.resultsList) return;

    this.narrowMax = Number(this.dataset.narrowMax || 109);
    this.mediumMax = Number(this.dataset.mediumMax || 125);
    this.selectedState = {};
    this.requiredParams = [
      "ride_duration",
      "ride_intensity",
      "bike_type",
      "rider_flexibility",
      "rider_stability",
      "bike_ebike",
    ];
    this.storageKey = this.dataset.fitRightStorageKey || `fit-right-v2:${window.location.pathname}`;
    this.productTemplateMap = this.templates.reduce((acc, node) => {
      acc[node.dataset.productTemplate] = node;
      return acc;
    }, {});

    this.optionButtons.forEach((button) => {
      button.setAttribute("role", "radio");
      button.setAttribute("aria-checked", "false");
    });

    this.init();
  }

  async init() {
    this.fitRightData = await this.loadFitRightData();
    if (!this.fitRightData) {
      this.setFeedback("Fit Right data could not be loaded.", "error");
      return;
    }

    this.bindEvents();
    this.restoreSuccessfulSubmission();
  }

  async loadFitRightData() {
    if (window.customFitRightV2Data && typeof window.customFitRightV2Data === "object") {
      return window.customFitRightV2Data;
    }

    if (this.dataEl && this.dataEl.textContent.trim()) {
      try {
        return JSON.parse(this.dataEl.textContent);
      } catch (error) {
        console.error("Fit Right inline JSON could not be parsed.", error);
      }
    }

    const assetUrl = this.dataset.fitRightDataUrl;
    if (!assetUrl) {
      return null;
    }

    try {
      const response = await fetch(assetUrl, { credentials: "same-origin" });
      if (!response.ok) throw new Error("HTTP " + response.status);
      return await response.json();
    } catch (error) {
      console.error("Fit Right JSON asset could not be loaded.", error);
      return null;
    }
  }

  toCanonicalBoolean(value) {
    const lower = String(value || "").trim().toLowerCase();
    if (["true", "1", "yes"].includes(lower)) return "true";
    if (["false", "0", "no"].includes(lower)) return "false";
    return lower;
  }

  toCanonicalSitboneBucket(value) {
    const lower = String(value || "").trim().toLowerCase();
    const legacy = {
      "80-109": "narrow",
      "110-125": "medium",
      "126-170": "wide",
    };
    return legacy[lower] || lower;
  }

  buildKey(params, paramOrder) {
    return paramOrder.map((name) => (params[name] || "").trim().toLowerCase()).join("|");
  }

  findOverride(overrides, state) {
    if (!Array.isArray(overrides)) return null;
    return (
      overrides.find((override) => {
        const match = override && override.match ? override.match : {};
        return Object.keys(match).every((key) => {
          return String(state[key] || "").toLowerCase() === String(match[key] || "").toLowerCase();
        });
      }) || null
    );
  }

  getWidthBucketFromMm(mm) {
    if (mm <= this.narrowMax) return "narrow";
    if (mm <= this.mediumMax) return "medium";
    return "wide";
  }

  setFeedback(message, type) {
    this.feedback.hidden = !message;
    this.feedback.textContent = message || "";
    this.feedback.classList.remove("is-error", "is-success");
    if (type) this.feedback.classList.add(type === "error" ? "is-error" : "is-success");
  }

  hideResults() {
    this.resultsContainer.hidden = true;
    this.resultsList.innerHTML = "";
  }

  invalidateRecommendations() {
    this.hideResults();
    this.setFeedback("");
  }

  setSelectedOption(param, value) {
    if (!param) return;
    this.selectedState[param] = value;

    this.optionButtons.forEach((button) => {
      if (button.dataset.param !== param) return;
      const isSelected = button.dataset.value === value;
      button.classList.toggle("is-selected", isSelected);
      button.setAttribute("aria-checked", isSelected ? "true" : "false");
    });
  }

  saveSuccessfulSubmission(state) {
    if (!state || typeof state !== "object") return;
    try {
      window.localStorage.setItem(
        this.storageKey,
        JSON.stringify({
          version: 1,
          savedAt: Date.now(),
          state: state,
        }),
      );
    } catch (error) {
      // Ignore storage failures (private mode, quota, etc.).
    }
  }

  loadSuccessfulSubmission() {
    try {
      const raw = window.localStorage.getItem(this.storageKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== "object" || !parsed.state || typeof parsed.state !== "object") {
        return null;
      }
      return parsed.state;
    } catch (error) {
      return null;
    }
  }

  clearSuccessfulSubmission() {
    try {
      window.localStorage.removeItem(this.storageKey);
    } catch (error) {
      // Ignore storage failures.
    }
  }

  restoreSuccessfulSubmission() {
    const savedState = this.loadSuccessfulSubmission();
    if (!savedState) return;

    this.requiredParams.forEach((param) => {
      const value = String(savedState[param] || "").trim().toLowerCase();
      if (!value) return;
      this.setSelectedOption(param, value);
    });

    const sitboneMm = Number(savedState.rider_sitbone_mm);
    if (Number.isFinite(sitboneMm)) {
      this.sitboneInput.value = String(Math.round(sitboneMm));
    }

    const payload = this.resolveRecommendations({ silent: true });
    if (!payload) {
      this.clearSuccessfulSubmission();
      this.invalidateRecommendations();
      return;
    }

    this.renderResults(payload.results, payload.state);
  }

  bindEvents() {
    this.optionButtons.forEach((button) => {
      button.addEventListener("click", () => {
        const param = button.dataset.param;
        const value = button.dataset.value;
        if (!param) return;

        this.setSelectedOption(param, value);

        this.invalidateRecommendations();
      });
    });

    this.sitboneInput.addEventListener("input", () => {
      this.invalidateRecommendations();
    });

    this.sitboneInput.addEventListener("change", () => {
      this.invalidateRecommendations();
    });

    this.form.addEventListener("submit", (event) => {
      event.preventDefault();
      const payload = this.resolveRecommendations();
      if (!payload) return;

      this.renderResults(payload.results, payload.state);
      this.saveSuccessfulSubmission(payload.state);
      this.resultsContainer.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }

  resolveRecommendations(options = {}) {
    const silent = Boolean(options.silent);
    const missing = this.requiredParams.filter((param) => !this.selectedState[param]);
    if (missing.length > 0) {
      if (!silent) this.setFeedback("Please answer all options before showing recommendations.", "error");
      return null;
    }

    const sitboneMm = Number(this.sitboneInput.value);
    if (!Number.isFinite(sitboneMm) || sitboneMm < 80 || sitboneMm > 170) {
      if (!silent) this.setFeedback("Please enter a sit bone width between 80 and 170 mm.", "error");
      return null;
    }

    const state = {
      ...this.selectedState,
      rider_sitbone_width: this.getWidthBucketFromMm(sitboneMm),
      rider_sitbone_mm: String(sitboneMm),
    };

    state.bike_ebike = this.toCanonicalBoolean(state.bike_ebike);
    state.rider_sitbone_width = this.toCanonicalSitboneBucket(state.rider_sitbone_width);
    state.bike_type = String(state.bike_type || "").trim().toLowerCase();

    const rules = this.fitRightData.rules || {};
    const override = this.findOverride(rules.overrides, state);
    if (override && override.apply && Array.isArray(override.apply.results)) {
      return {
        source: "override",
        state: state,
        note:
          state.bike_type === "urban"
            ? "Urban selected. Using dedicated Urban + E-bike recommendations."
            : "",
        results: override.apply.results,
      };
    }

    const aliases = rules.bike_type_aliases || {};
    const bikeAlias = aliases[state.bike_type];
    const normalized = { ...state };
    if (bikeAlias && bikeAlias.maps_to) {
      normalized.bike_type = String(bikeAlias.maps_to).toLowerCase();
    }

    const key = this.buildKey(normalized, this.fitRightData.param_order || []);
    const match = (this.fitRightData.base_lookup || {})[key];
    if (!match || !Array.isArray(match.results)) {
      if (!silent) this.setFeedback("We could not find a recommendation for this combination.", "error");
      return null;
    }

    return {
      source: "base_lookup",
      state: state,
      note: state.bike_type === "urban" ? (bikeAlias && bikeAlias.note) || "Urban selected." : "",
      results: match.results,
    };
  }

  buildLineItemProperties(state) {
    if (!state || typeof state !== "object") return {};

    const ebikeValue =
      state.bike_ebike === "true" ? "yes" : state.bike_ebike === "false" ? "no" : String(state.bike_ebike || "");

    return {
      _source: "fit_right",
      _fr_bike_type: state.bike_type,
      _fr_ride_duration: state.ride_duration,
      _fr_ride_intensity: state.ride_intensity,
      _fr_ebike: ebikeValue,
      _fr_flexibility: state.rider_flexibility,
      _fr_stability: state.rider_stability,
      _fr_sitbone_width_bucket: state.rider_sitbone_width,
      _fr_sitbone_mm: state.rider_sitbone_mm,
    };
  }

  applyLineItemProperties(card, state) {
    const forms = Array.from(card.querySelectorAll("form"));
    if (forms.length === 0) return;

    const properties = this.buildLineItemProperties(state);
    forms.forEach((form) => {
      const existing = form.querySelectorAll("[data-fit-right-line-item-property]");
      existing.forEach((node) => node.remove());

      Object.entries(properties).forEach(([key, value]) => {
        if (value === null || value === undefined || String(value).trim() === "") return;
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = `properties[${key}]`;
        input.value = String(value);
        input.setAttribute("data-fit-right-line-item-property", "true");
        form.appendChild(input);
      });
    });
  }

  renderResults(results, state) {
    this.resultsList.innerHTML = "";

    let renderedCount = 0;
    results.forEach((result, index) => {
      const template = this.productTemplateMap[result.product_handle];
      if (!template) return;

      const card = template.cloneNode(true);
      card.removeAttribute("data-product-template");

      const optionsModal = card.querySelector("x-modal[id]");
      const optionsTrigger = card.querySelector(".fit-right-v2__result-see-options");
      if (optionsModal && optionsTrigger) {
        const baseModalId = optionsModal.getAttribute("id") || "fit-right-options";
        const uniqueModalId = `${baseModalId}-result-${index + 1}`;
        optionsModal.setAttribute("id", uniqueModalId);
        optionsTrigger.setAttribute("aria-controls", uniqueModalId);
      }

      const rankEl = card.querySelector("[data-fit-right-rank]");
      const reasonEl = card.querySelector("[data-fit-right-reason]");
      const widthEl = card.querySelector("[data-fit-right-width]");

      if (rankEl) rankEl.textContent = "Recommendation #" + (index + 1);
      if (reasonEl) {
        reasonEl.innerHTML = "";
        const reasonText = String(result.reason || "").trim();
        if (reasonText) {
          const prefixEl = document.createElement("span");
          prefixEl.className = "fit-right-v2__result-reason-prefix";
          prefixEl.textContent = "Why this one for you? ";
          reasonEl.appendChild(prefixEl);
          reasonEl.appendChild(document.createTextNode(reasonText));
        }
      }
      if (widthEl) {
        if (result.width_filter) {
          widthEl.hidden = false;
          widthEl.textContent = result.width_filter;
        } else {
          widthEl.hidden = true;
          widthEl.textContent = "";
        }
      }

      const variantRows = Array.from(card.querySelectorAll("[data-variant-row]"));
      if (variantRows.length > 0 && result.width_filter) {
        const widthFilterLower = String(result.width_filter).toLowerCase();

        variantRows.forEach((row) => {
          const titleLower = String(row.dataset.variantTitleLower || "").toLowerCase();
          row.hidden = !titleLower.includes(widthFilterLower);
        });
      } else {
        variantRows.forEach((row) => {
          row.hidden = false;
        });
      }

      this.applyLineItemProperties(card, state);
      this.resultsList.appendChild(card);
      renderedCount += 1;
    });

    this.resultsContainer.hidden = false;
    if (renderedCount === 0) {
      this.setFeedback("No matching products were found in the saddles collection.", "error");
    } else {
      this.setFeedback("");
    }
  }
}

if (!window.customElements.get("fit-right-v2")) {
  window.customElements.define("fit-right-v2", FitRightV2);
}
